import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import {
  getPrinterAgentStatus,
  connectPrinterAgent,
  stopPrinterAgent,
  checkWhetherAgentRunsLoc,
} from "../config/Services";
import { useSnackbar } from "../layouts/ecommerce/product/hooks/SnackbarProvider";

const PrinterAgentContext = createContext(null);

PrinterAgentContext.displayName = "PrinterAgentContext";

const PRINTER_AGENT_CONNECTED_KEY = "printerAgentConnected";
const SEND_TO_KITCHEN_KEY = "sendToKitchen";
const POLLING_INTERVAL = 30000; // 30 seconds for localhost polling
const SERVER_POLLING_INTERVAL = 60000; // 60 seconds for server status polling

// Helper to check if retailType is RESTAURANT
const isRestaurantBranch = () => {
  const retailType = localStorage.getItem("retailType");
  return retailType === "RESTAURANT";
};

// Helper to check if cloud print is enabled
const isCloudPrintEnabled = () => {
  try {
    const appConfig = JSON.parse(localStorage.getItem("appConfig"));
    return appConfig?.all?.CLOUD_PRINT?.flag === true;
  } catch {
    return false;
  }
};

// Helper to check if user is logged in
const isUserLoggedIn = () => {
  const accessToken = localStorage.getItem("access_token");
  return !!accessToken;
};

// Helper to get connection payload for starting the agent
const getConnectionPayload = () => {
  const retailUserLocalStorageData = JSON.parse(localStorage.getItem("retailUserDetails") || "{}");
  return {
    locationId: localStorage.getItem("locId"),
    orgId: localStorage.getItem("orgId"),
    at: localStorage.getItem("access_token"),
    createdBy: retailUserLocalStorageData?.uidx,
    createdByName: retailUserLocalStorageData?.firstName,
  };
};

export const PrinterAgentProvider = ({ children }) => {
  // isAgentAvailable: true if printer agent app is running on localhost
  const [isAgentAvailable, setIsAgentAvailable] = useState(false);
  // isAgentRunning: true if status API returns success (agent is enabled)
  const [isAgentRunning, setIsAgentRunning] = useState(false);
  // isChecking: true while checking status
  const [isChecking, setIsChecking] = useState(false);
  // hasInitialCheckDone: true after the first status check
  const [hasInitialCheckDone, setHasInitialCheckDone] = useState(false);
  // lastChecked: timestamp of last status check
  const [lastChecked, setLastChecked] = useState(null);
  // error: error message if status check fails
  const [error, setError] = useState(null);
  // isPolling: true if polling is active
  const [isPolling, setIsPolling] = useState(false);
  // showStartButton: true if agent needs to be started (data has more than application-status)
  const [showStartButton, setShowStartButton] = useState(false);
  // showEnableButton: true if agent is disabled (data contains only application-status)
  const [showEnableButton, setShowEnableButton] = useState(false);
  // isPrinterOnlineForLocation: true if printer is ONLINE for this location (from server API)
  const [isPrinterOnlineForLocation, setIsPrinterOnlineForLocation] = useState(false);

  const showSnackbar = useSnackbar();

  const pollingRef = useRef(null);
  const serverPollingRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Stop localhost polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
      setIsPolling(false);
    }
  }, []);

  // Stop server polling
  const stopServerPolling = useCallback(() => {
    if (serverPollingRef.current) {
      clearInterval(serverPollingRef.current);
      serverPollingRef.current = null;
    }
  }, []);

  // Check if printer is online for this location (server API)
  const checkServerPrinterStatus = useCallback(async () => {
    // Don't check if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      localStorage.setItem(SEND_TO_KITCHEN_KEY, "false");
      return { success: false, isOnline: false };
    }

    try {
      const locId = localStorage.getItem("locId");
      const orgId = localStorage.getItem("orgId");

      const response = await checkWhetherAgentRunsLoc(locId, orgId);

      if (
        response &&
        (response?.data?.statusCode === 200 ||
          response?.data?.statusCode === 201 ||
          response?.data?.es === 0)
      ) {
        const printerStatus = response?.data?.data?.status;
        const isOnline = printerStatus !== "OFFLINE";
        setIsPrinterOnlineForLocation(isOnline);
        localStorage.setItem(SEND_TO_KITCHEN_KEY, isOnline ? "true" : "false");
        return { success: true, isOnline };
      } else {
        setIsPrinterOnlineForLocation(false);
        localStorage.setItem(SEND_TO_KITCHEN_KEY, "false");
        return { success: false, isOnline: false };
      }
    } catch (err) {
      setIsPrinterOnlineForLocation(false);
      localStorage.setItem(SEND_TO_KITCHEN_KEY, "false");
      return { success: false, isOnline: false };
    }
  }, []);

  // Start server polling every 10 seconds (for cart page only)
  const startServerPolling = useCallback(() => {
    // Don't start if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      return;
    }

    // Don't start if already polling
    if (serverPollingRef.current) {
      return;
    }

    // Skip for non-restaurant branch types
    if (!isRestaurantBranch()) {
      return;
    }

    // Initial check
    checkServerPrinterStatus();

    serverPollingRef.current = setInterval(() => {
      checkServerPrinterStatus();
    }, SERVER_POLLING_INTERVAL);
  }, [checkServerPrinterStatus]);

  // Check printer agent status API
  const checkPrinterAgentStatus = useCallback(async () => {
    // Don't check if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      return { success: false, running: false };
    }

    try {
      setIsChecking(true);
      setError(null);

      const response = await getPrinterAgentStatus();

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        // Check if data is empty (printer is running)
        const printerData = response?.data?.data;
        const isPrinterRunning =
          !printerData ||
          (Array.isArray(printerData) && printerData.length === 0) ||
          (typeof printerData === "object" && Object.keys(printerData).length === 0);

        if (isPrinterRunning) {
          // Agent is available and printer is running
          setIsAgentAvailable(true);
          setIsAgentRunning(true);
          setShowStartButton(false);
          setShowEnableButton(false);
          setLastChecked(new Date().toISOString());
          setIsChecking(false);
          localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "true");
          return { success: true, running: true };
        } else {
          // Check if data contains only application-status (agent is disabled)
          // Handle both array format: ["application-status"] and object format: {"application-status": ...}
          let hasOnlyApplicationStatus = false;
          let hasApplicationStatus = false;

          if (Array.isArray(printerData)) {
            // Array format: ["application-status"] or ["application-status", "other-stuff"]
            hasApplicationStatus = printerData?.includes("application-status");
            hasOnlyApplicationStatus =
              printerData?.length === 1 && printerData[0] === "application-status";
          } else if (typeof printerData === "object") {
            // Object format: {"application-status": ...}
            const dataKeys = Object.keys(printerData);
            hasApplicationStatus = dataKeys?.includes("application-status");
            hasOnlyApplicationStatus =
              dataKeys?.length === 1 && dataKeys[0] === "application-status";
          }

          if (hasOnlyApplicationStatus) {
            // Agent is disabled - show enable button
            setIsAgentAvailable(true);
            setIsAgentRunning(false);
            setShowStartButton(false);
            setShowEnableButton(true);
            setLastChecked(new Date().toISOString());
            setIsChecking(false);
            localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
            return { success: true, running: false, showEnableButton: true };
          } else if (hasApplicationStatus) {
            // Agent has application-status but also other data - show start button
            setIsAgentAvailable(true);
            setIsAgentRunning(false);
            setShowStartButton(true);
            setShowEnableButton(false);
            setLastChecked(new Date().toISOString());
            setIsChecking(false);
            localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
            return { success: true, running: false, showStartButton: true };
          } else {
            // No application-status present, just show start button
            setIsAgentAvailable(true);
            setIsAgentRunning(false);
            setShowStartButton(true);
            setShowEnableButton(false);
            setLastChecked(new Date().toISOString());
            setIsChecking(false);
            localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
            return { success: true, running: false, showStartButton: true };
          }
        }
      } else {
        // API returned but agent is disabled
        setIsAgentAvailable(true);
        setIsAgentRunning(false);
        setShowEnableButton(false);
        showSnackbar("Printer agent is disabled.", "error");
        setLastChecked(new Date().toISOString());
        setIsChecking(false);
        localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
        return { success: true, running: false };
      }
    } catch (err) {
      // Network error - agent not running on localhost (HTTPS error)
      setIsAgentAvailable(false);
      setIsAgentRunning(false);
      setShowStartButton(false);
      setShowEnableButton(false);
      setError("The agent is not running in your system. Please run the agent.");
      setLastChecked(new Date().toISOString());
      setIsChecking(false);
      localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
      // Only return success: false for HTTPS errors to stop polling
      return { success: false, running: false, isHttpsError: true };
    }
  }, []);

  // Start polling every 30 seconds
  const startPolling = useCallback(() => {
    // Don't start if already polling
    if (pollingRef.current) return;

    setIsPolling(true);
    pollingRef.current = setInterval(async () => {
      const result = await checkPrinterAgentStatus();
      // If agent becomes unavailable (error), stop polling
      if (!result.success) {
        stopPolling();
      }
    }, POLLING_INTERVAL);
  }, [checkPrinterAgentStatus, stopPolling]);

  // Initialize printer agent - call this on location selection page (only for restaurants)
  const initializePrinterAgent = useCallback(async () => {
    // Don't initialize multiple times
    if (hasInitializedRef.current) return;

    // Skip if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      setHasInitialCheckDone(true);
      localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
      localStorage.setItem(SEND_TO_KITCHEN_KEY, "false");
      return;
    }

    // Skip for non-restaurant branch types
    if (!isRestaurantBranch()) {
      setHasInitialCheckDone(true);
      localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
      localStorage.setItem(SEND_TO_KITCHEN_KEY, "false");
      return;
    }

    hasInitializedRef.current = true;
    setHasInitialCheckDone(true);

    // Start server polling for cart and table layout pages
    startServerPolling();
  }, [startServerPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      stopServerPolling();
    };
  }, [stopPolling, stopServerPolling]);

  // Retry connection - for manual retry from UI
  const retryConnection = useCallback(async () => {
    // Don't retry if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      return false;
    }

    setError(null);
    const result = await checkPrinterAgentStatus();

    // If retry succeeds, start polling
    if (result.success) {
      startPolling();
    }

    return result.success;
  }, [checkPrinterAgentStatus, startPolling]);

  // Set printer agent as connected (called when enabling)
  const setConnected = useCallback(() => {
    localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "true");
    setIsAgentRunning(true);
    // Start polling if not already polling
    if (!pollingRef.current) {
      startPolling();
    }
  }, [startPolling]);

  // Set printer agent as disconnected (called when disabling)
  const setDisconnected = useCallback(() => {
    localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
    setIsAgentRunning(false);
    // Stop polling when disabled
    stopPolling();
  }, [stopPolling]);

  // Reset state (called on logout)
  const resetPrinterAgent = useCallback(() => {
    stopPolling();
    stopServerPolling();
    hasInitializedRef.current = false;
    setIsAgentAvailable(false);
    setIsAgentRunning(false);
    setHasInitialCheckDone(false);
    setLastChecked(null);
    setError(null);
    setIsPolling(false);
    setShowStartButton(false);
    setShowEnableButton(false);
    setIsPrinterOnlineForLocation(false);
    localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "false");
    localStorage.setItem(SEND_TO_KITCHEN_KEY, "false");
  }, [stopPolling, stopServerPolling]);

  // Start agent - called when user clicks start button
  const startAgent = useCallback(async () => {
    // Don't start if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      return { success: false };
    }

    try {
      setIsChecking(true);
      setError(null);
      const payload = getConnectionPayload();
      const response = await connectPrinterAgent(payload);

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        setShowStartButton(false);
        setShowEnableButton(false);
        setIsAgentRunning(true);
        localStorage.setItem(PRINTER_AGENT_CONNECTED_KEY, "true");
        setIsChecking(false);
        return { success: true };
      } else {
        showSnackbar("Failed to start printer agent", "error");
        setIsChecking(false);
        return { success: false };
      }
    } catch (err) {
      setError(err.message || "Failed to start printer agent");
      setIsChecking(false);
      return { success: false };
    }
  }, []);

  // Enable agent - called when user clicks enable button (when agent is disabled)
  const enableAgent = useCallback(async () => {
    // Don't enable if cloud print is not enabled
    if (!isCloudPrintEnabled()) {
      return { success: false };
    }

    try {
      setIsChecking(true);
      setError(null);
      const response = await stopPrinterAgent(true); // true = enable the agent

      if (response && (response?.data?.statusCode === 200 || response?.data?.es === 0)) {
        setShowEnableButton(false);
        setIsChecking(false);
        // After enabling, check status again to determine next state
        await checkPrinterAgentStatus();
        return { success: true };
      } else {
        showSnackbar("Failed to enable printer agent", "error");
        setIsChecking(false);
        return { success: false };
      }
    } catch (err) {
      setError(err.message || "Failed to enable printer agent");
      setIsChecking(false);
      return { success: false };
    }
  }, [checkPrinterAgentStatus]);

  const value = {
    // State
    isAgentAvailable,
    isAgentRunning,
    isChecking,
    hasInitialCheckDone,
    lastChecked,
    error,
    isPolling,
    showStartButton,
    showEnableButton,
    isPrinterOnlineForLocation,

    // Actions
    initializePrinterAgent, // Call this on location selection page
    checkPrinterAgentStatus,
    checkServerPrinterStatus, // Check server status for this location
    retryConnection,
    setConnected,
    setDisconnected,
    startPolling,
    stopPolling,
    startServerPolling, // Start polling server status (for cart and table layout)
    stopServerPolling, // Stop polling server status
    resetPrinterAgent,
    startAgent, // Call this when user clicks start button
    enableAgent, // Call this when user clicks enable button (agent is disabled)
  };

  return <PrinterAgentContext.Provider value={value}>{children}</PrinterAgentContext.Provider>;
};

export const usePrinterAgentStatus = () => {
  const context = useContext(PrinterAgentContext);

  if (!context) {
    throw new Error("usePrinterAgentStatus must be used within a PrinterAgentProvider");
  }

  return context;
};

export default PrinterAgentContext;
